import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { RegionSelectService } from 'gain-web/shared-modules/ga-region-select/region-select.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

export class GaRegionSelectChange {
  public readonly source: GaRegionSelectComponent;
  public readonly value: string[];

  constructor(source: GaRegionSelectComponent, value: string[]) {
    this.source = source;
    this.value = value;
  }
}

@Component({
  selector: 'app-region-select',
  templateUrl: './region-select.component.html',
  styleUrls: ['./region-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GaRegionSelectComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject();
  protected displayValue$ = new BehaviorSubject<string | null>(null);

  @Input()
  label = 'Regions';

  @Input()
  placeholder = 'Select regions';

  @Input()
  countryCode: string = '';

  @Input()
  dialogTitle?: string;

  @Input()
  set disabled(disable: boolean) {
    if (disable && !this.disabled) {
      this.regionsFormControl.disable();
    }
    if (!disable && this.disabled) {
      this.regionsFormControl.enable();
    }
  }

  get disabled() {
    return this.regionsFormControl.disabled;
  }

  @Input()
  set enabled(enable: boolean) {
    this.disabled = !enable;
  }

  get enabled() {
    return !this.disabled;
  }

  @Input()
  readOnlyReasonTooltip?: string = '';

  @Input()
  readonly regionsFormControl: FormControl<string[]> = new FormControl<
    string[]
  >([], {
    nonNullable: true,
  });

  @Input()
  set required(required: boolean) {
    if (required && !this.required) {
      this.regionsFormControl.addValidators(Validators.required);
    }

    if (!required && this.required) {
      this.regionsFormControl.removeValidators(Validators.required);
    }
    this.regionsFormControl.updateValueAndValidity();
  }

  get required() {
    return this.regionsFormControl.hasValidator(Validators.required);
  }

  requiredErrorMessage = input<string>('At least one region must be selected');

  @Input()
  set value(value: string[]) {
    this._setCountries(value, { emitEvent: false });
  }

  @Output()
  selectionChange = new EventEmitter<GaRegionSelectChange>();

  get regions() {
    if (this.regionsFormControl == null) {
      throw new Error('missing form control');
    }
    return this.regionsFormControl;
  }

  constructor(
    private _regionSelect: RegionSelectService,
    private _cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.regions.valueChanges
      .pipe(startWith(this.regions.value), takeUntil(this.onDestroy$))
      .subscribe(() => {
        this._refreshDisplayValue();
      });
  }

  private _refreshDisplayValue() {
    this.displayValue$.next(this.getDisplayValue());
  }

  getDisplayValue() {
    const displayValue =
      this.regions.value.length === 1
        ? '1 region selected'
        : this.regions.value.length > 1
          ? `${this.regions.value.length} regions selected`
          : '';
    return displayValue;
  }

  public async selectRegions() {
    const result = await this._regionSelect.selectRegions({
      selectedRegions: this.regions.value,
      dialogTitle: this.dialogTitle,
      countryCode: this.countryCode,
    });
    if (!result.cancelled && 'regions' in result) {
      this._setCountries(result.regions);
      this.regionsFormControl.markAsTouched();
    }
    this.regionsFormControl.updateValueAndValidity();
  }

  protected async _openRegionSelectDialog(ev: MouseEvent | Event) {
    ev.preventDefault();
    await this.selectRegions();
  }

  private _setCountries(
    countries: string[],
    options?: { emitEvent?: boolean },
  ) {
    this.regions.setValue(countries);
    if (options?.emitEvent !== false) {
      this.selectionChange.emit(new GaRegionSelectChange(this, countries));
    }
  }
  ngOnDestroy() {
    this.onDestroy$.next(undefined);
    this.onDestroy$.complete();
  }
}
